import { useMemo } from 'react';
import { LifeCover } from '@app/domain/model/eshop/life/LifeCover';
import { SelectedCover } from '@app/domain/model/eshop/life/SelectedCover';
import { Coverage } from '@app/domain/model/Coverage';
import { getAvailableATDOptions } from '@app/app/esales/life/_sections/steps/CustomCoverSelectionStep/getATDBasedMinimums';

const CHOICE_BUTTON_MAX_OPTIONS = 3;
const RANGE_SLIDER_STEP = 1000;

export function useOpenCoverConfig(
    openCoverCode: Nullable<string>,
    covers: LifeCover[],
    selectedCovers: SelectedCover[],
) {
    return useMemo(() => {
        if (!openCoverCode) {
            return null;
        }

        const cover = covers.find(cover => cover.code === openCoverCode);
        const selectedCover = selectedCovers.find(selectedCover => selectedCover.code === openCoverCode);

        if (!cover) {
            return null;
        }

        const hasOptions = cover.options && cover.options.length > 0;
        const canUseChoiceButtons =
            hasOptions && cover.options.length >= 1 && cover.options.length <= CHOICE_BUTTON_MAX_OPTIONS;

        let disabledOptions: number[] = [];
        if (cover.code === Coverage.TemporaryDisabilityByAccident && hasOptions) {
            const diCover = selectedCovers.find(sc => sc.code === Coverage.LifeInsurance);
            const apdCover = selectedCovers.find(sc => sc.code === Coverage.DisabilityByAccident);
            const diValue = diCover?.value ?? 0;
            const apdValue = apdCover?.value ?? 0;
            const atdOptionsResult = getAvailableATDOptions(diValue, apdValue, cover.options);
            disabledOptions = atdOptionsResult.disabledOptions;
        }

        const min = cover.min;
        const max = cover.max;
        const step = RANGE_SLIDER_STEP;

        return {
            cover,
            selectedCover,
            min,
            max,
            step,
            default: cover.default,
            canUseChoiceButtons,
            options: canUseChoiceButtons ? cover.options : [],
            disabledOptions,
        };
    }, [openCoverCode, covers, selectedCovers]);
}
